import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const InvoiceForTrue = ({ invoiceArray, firmArray, items, taxBreakup, taxSummary, HSNSummaryData }) => {
  // Get data from sessionStorage if props are not provided
  const [sessionData, setSessionData] = useState(null);
  
  useEffect(() => {
    // Check if we need to get data from sessionStorage
    if (!invoiceArray?.length && !sessionData) {
      try {
        const storedData = sessionStorage.getItem('invoiceData');
        const storedHSNSummaryData = sessionStorage.getItem('HSNSummaryData');
        const storedEInvoiceData = sessionStorage.getItem('EInvoiceData');
        if (storedData) {
          const parsedData = JSON.parse(storedData);
          const parsedHSNSummaryData = JSON.parse(storedHSNSummaryData);
          const parsedEInvoiceData = JSON.parse(storedEInvoiceData);
          setSessionData({...parsedData, HSNSummaryData: parsedHSNSummaryData?.HSNSummaryData, EInvoiceData: parsedEInvoiceData?.EInvoiceData});
          // Clear the session storage to avoid data persisting
          sessionStorage.removeItem('invoiceData');
          //here add that 
          // Auto-print after data is loaded and invoice format is filled
          setTimeout(() => {
            console.log('Invoice format filled, triggering print...');
            window.print();
          }, 1500); // Wait 1.5 seconds for invoice format to be completely filled
        }
      } catch (error) {
        console.error('Error retrieving data from sessionStorage:', error);
      }
    }
  }, [invoiceArray, sessionData]);

  // Use props or session data
  const effectiveInvoiceArray = invoiceArray?.length ? invoiceArray : sessionData?.invoiceArray || [];
  const effectiveFirmArray = firmArray?.length ? firmArray : sessionData?.firmArray || [];
  const effectiveItems = items || sessionData?.items || [];
  const effectiveTaxBreakup = taxBreakup || sessionData?.taxBreakup || [];
  const effectiveTaxSummary = taxSummary || sessionData?.taxSummary || [];
  const effectiveHSNSummaryData =  sessionData?.HSNSummaryData || [];
  const effectiveEInvoiceData =  sessionData?.EInvoiceData || [];
  console.log("HSNSummaryData", sessionData?.HSNSummaryData);
  
  // Use the first elements from the arrays if they exist
  const invoiceData = effectiveInvoiceArray?.[0] || {};
  const firmData = effectiveFirmArray?.[0] || {};

  // Check if we need pagination (more than 20 items)
  const needsPagination = effectiveItems.length > 20;

  // Data object derived from props or session data
  const data = {
    firm: {
      GST: firmData?.GST || '',
      FirmName: firmData?.FirmName || '',
      Address1: firmData?.Address1 || '',
      Address2: firmData?.Address2 || '',
      Mobile: firmData?.MobileNo || '',
      BankName: firmData?.BankName || '',
      AccountNo: firmData?.AccountNo || '',
      IFSC: firmData?.IFSC || '',
      MsmeNo: firmData?.MsmeNo || '',
    },
    buyer: {
      name: invoiceData?.PartyName || '',
      address: invoiceData?.Address1 || '',
      address2: invoiceData?.Address2 || '',
      gst: invoiceData?.GSTPO ? invoiceData.GSTPO.replace('GST No- ', '') : '',
      invoiceNo: invoiceData?.VoucherNo || '',
      invoiceDate: invoiceData?.Voucher_Date || '',
      challanNo: invoiceData?.ChallanNo || 'SEZ',
      challanDate: invoiceData?.Challan_Date || '',
      vehicle: invoiceData?.VehicleNo || '',
      poNo: invoiceData?.PONo || '',
      transport: invoiceData?.Transport || '',
      placeOfSupply: invoiceData?.PlaceOfSupply || '',
      CrDays: invoiceData?.CrDays || '',
      Signatory: invoiceData?.Signatory || '',
    },
    // Use effective items from props or session storage
    items: effectiveItems,
    // Use passed totals if available, otherwise generate default
    totals:  {
      taxable: invoiceData?.GrossAmount || 0,
      tax: invoiceData?.TaxationAmount || 0,
      cgst: invoiceData?.TaxationAmount ? invoiceData.TaxationAmount / 2 : 0,
      sgst: invoiceData?.TaxationAmount ? invoiceData.TaxationAmount / 2 : 0,
      roundOff: invoiceData?.RoundedValue || 0,
      grandTotal: invoiceData?.NetAmount || 0,
    },
    // Use effective tax data from props or session storage
    taxBreakup: effectiveTaxBreakup,
    taxSummary: effectiveTaxSummary,
    HSNSummaryData: effectiveHSNSummaryData,
    EInvoiceData: effectiveEInvoiceData
  };

  const tableHeaderStyle = {
    background: '#f0f0f0',
    fontWeight: 700,
    fontSize: 13,
    padding: '8px 5px',
    borderBottom: '2px solid #222',
    color: '#000',
  };
  
  const tableCellRight = { textAlign: 'right', color: '#000', padding: '6px 5px' };
  const tableCellCenter = { textAlign: 'center', color: '#000', padding: '6px 5px' };
  const tableCellLeft = { textAlign: 'left', color: '#000', padding: '6px 5px' };
  
  // Print-specific styles
  const printStyles = `
    @media print {
      /* Hide elements not meant for printing */
      .no-print {
        display: none !important;
      }
      
      /* Reset all margins and paddings */
      @page {
        size: A4;
        margin: 0 5mm !important;
      }
      
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        width: 100% !important;
        height: auto !important;
        color: black !important;
        -webkit-print-color-adjust: exact !important;
      }
      
      .page-content {
        padding: 0 !important;
        margin: 0 !important;
        background: white !important;
        color: black !important;
      }
      
      .print-container {
        border: none !important;
        box-shadow: none !important;
        padding: 7mm 10mm !important; /* Reduced from 10mm top padding to 7mm */
        margin: 0 !important;
        width: 100% !important;
        max-width: 100% !important;
        border-radius: 0 !important;
        background: white !important;
        color: black !important;
        transform: none !important;
        transform-origin: top left !important;
        box-sizing: border-box !important;
      }
      
      /* Handle pagination for the item table and post-items section */
      
      /* Make table headers repeat on each page */
      thead { 
        display: table-header-group !important;
      }
      
      /* Ensure the item rows can break across pages naturally */
      tr.item-row {
        page-break-inside: auto !important;
        line-height: 1.1 !important; /* Added line-height control */
      }
      
      /* For proper pagination of items */
      table { 
        page-break-inside: auto !important; 
        margin-top: 0 !important;  /* Remove top margin from tables */
        margin-bottom: 0 !important; /* Remove bottom margin from tables */
      }
      
      tr { 
        page-break-inside: avoid !important; 
      }
      
      /* Critical: Keep the post-items section together */
      .after-items-section {
        page-break-inside: avoid !important;
        padding-top: 5px !important; /* Reduced from 10px to 5px */
        margin-top: 8px !important; /* Reduced from 12px to 8px */
      }
      
      /* For proper spacing on the last page */
      .item-table-container {
        margin-bottom: ${needsPagination ? '0' : '3mm'} !important; /* Reduced from 5mm to 3mm */
        margin-top: 0 !important; /* Remove top margin */
      }
      
      /* Make table more compact when printing */
      table, tr, td, th {
        font-size: 11px !important;
        padding: 1px 3px !important; /* Reduced from 2px 3px to 1px 3px */
        line-height: 1.2 !important; /* Added line height control */
      }
      
      /* Adjust font sizes for printing */
      h1, h2, h3, h4, h5, h6 { margin: 2px 0 !important; }
      p, div { margin: 0 !important; line-height: 1.2 !important; }
      
      /* Make the header more compact */
      .invoice-header { margin-bottom: 3px !important; } /* Reduced from 5px to 3px */
      
      /* Adjust spacing between sections */
      .section-spacing { margin-top: 3px !important; margin-bottom: 3px !important; } /* Reduced from 5px to 3px */
      
      /* Preserve spacing for amount in words section */
      .amount-words-section { margin-top: 12px !important; margin-bottom: 5px !important; } /* Reduced from 25px to 12px */
      
      /* Horizontal lines */
      hr {
        margin: 5px 0 !important; /* Reduced margin */
      }
      
      /* Ensure background colors print */
      * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        color-adjust: exact !important;
        color: black !important;
      }
      
      /* Keep terms and conditions together */
      .terms-section {
        page-break-inside: avoid !important;
        margin-bottom: 5px !important; /* Added smaller margin */
      }
      
      .signature-section {
        page-break-inside: avoid !important;
        margin-top: 5px !important; /* Reduced from 10px to 5px */
      }
      
      /* Ensure signature space is maintained when printing, but smaller */
      .signature-name-row {
        margin-bottom: 0 !important;
        padding-bottom: 0 !important;
      }
      
      .signature-space {
        height: 15px !important; /* Reduced from 25px to 15px */
        min-height: 15px !important;
        display: block !important;
        visibility: visible !important;
      }
      
      .signature-title-row {
        margin-top: 0 !important;
        padding-top: 0 !important;
      }
      
      /* All table cells more compact */
      td, th {
        line-height: 1.2 !important;
      }
      
      /* More compact list */
      ol, ul {
        padding-left: 15px !important; /* Reduced from 20px */
        margin: 0 !important;
      }
      
      li {
        margin-bottom: 0 !important;
        line-height: 1.2 !important;
      }
    }
  `;

  // Function to handle printing
  const handlePrint = () => {
    window.print();
  };
  
  // Added state for copy type
  const [copyType, setCopyType] = useState("ORIGINAL");

  // Helper function to safely format numbers
  const formatNumber = (value) => {
    if (value === null || value === undefined) return '';
    const numValue = parseFloat(value);
    return isNaN(numValue) ? '' : numValue.toFixed(2);
  };

  // Create a table header component that can be reused for each page
  const ItemTableHeader = () => (
    <thead>
      <tr>
        <th style={{...tableHeaderStyle, fontSize: 12, padding: '4px 3px'}}>SN</th>
        <th style={{...tableHeaderStyle, fontSize: 12, padding: '4px 3px'}}>Code</th>
        <th style={{...tableHeaderStyle, fontSize: 12, padding: '4px 3px'}}>Description</th>
        <th style={{...tableHeaderStyle, fontSize: 12, padding: '4px 3px'}}>Ply</th>
        <th style={{...tableHeaderStyle, fontSize: 12, padding: '4px 3px'}}>Calc</th>
        <th style={{...tableHeaderStyle, fontSize: 12, padding: '4px 3px'}}>Qty</th>
        <th style={{...tableHeaderStyle, fontSize: 12, padding: '4px 3px'}}>Rate</th>
        <th style={{...tableHeaderStyle, fontSize: 12, padding: '4px 3px'}}>Amount</th>
      </tr>
    </thead>
  );

  // Adjust signature space
  const signature = (
    <div className="signature-section" style={{ width: '30%', fontSize: 12, color: '#000' }}>
      <div style={{ textAlign: 'right', fontWeight: 600 }}>
        <div className="signature-name-row">For <b>{data.firm.FirmName}</b></div>
        {/* This is a spacer specifically for signatures */}
        <div className="signature-space" style={{ height: 20, border: '0px dashed transparent' }}></div>
        <div className="signature-title-row">Authorized Signatory</div>
      </div>
    </div>
  );

  // --- NEW LAYOUT STYLES ---
  const pageStyle = {
    width: '21.6cm',
    height: '27.9cm',
    position: 'relative',
    background: '#fff',
    margin: 0,
    boxSizing: 'border-box',
    overflow: 'hidden',
    fontFamily: 'Segoe UI, Arial, sans-serif',
    color: '#000',
    fontSize: '12px',
    border: '1px solid #222',
    padding: 0,
  };

  // Remove all heading backgrounds and borders, only show data
  const invoiceDetailsStyle = {
    position: 'absolute',
    top: '0.8cm',
    left: '17.5cm',
    width: '7cm',
    height: '4cm',
    boxSizing: 'border-box',
    padding: 0,
    background: 'transparent',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    fontSize: '0.25cm',
    lineHeight: 1,
  };
  const receiverConsigneeContainer = {
    position: 'absolute',
    top: '5.5cm',
    left: '2cm',
    width: '19.6cm',
    height: '3cm',
    display: 'flex',
    flexDirection: 'row',
    gap: '0cm',
  };
  const receiverConsigneeBox = {
    width: '9.8cm',
    height: '3cm',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    background: 'transparent',
  };
  const receiverConsigneeHeading = {
    height: '0.5cm',
    fontWeight: 700,
    fontSize: '13px',
    background: 'transparent',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '0.2cm',
    borderBottom: 'none',
  };
  const receiverConsigneeData = {
    height: '2.5cm',
    padding: 0,
    fontSize: '12px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    background: 'transparent',
  };
  const itemTableContainer = {
    position: 'absolute',
    top: '8.8cm',
    left: '1cm',
    width: '19.7cm',
    height: '9cm',
    boxSizing: 'border-box',
    background: 'transparent',
    display: 'flex',
    flexDirection: 'column',
  };
  const itemTableHeaderRow = {
    display: 'flex',
    flexDirection: 'row',
    height: '1cm',
    fontWeight: 700,
    fontSize: '12px',
    alignItems: 'center',
    background: 'transparent',
    borderBottom: 'none',
  };
  const itemTableDataArea = {
    height: '8.2cm',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    background: 'transparent',
    padding: 0,
  };
  const colWidths = ['1.3cm','1.9cm','7.3cm','1.9cm','1.2cm','1.2cm','1.9cm','2.9cm'];

  return (
    <div className="page-content">
      <div style={pageStyle} className="only-print">
        {/* Invoice Details Box */}
        <div style={{
  position: 'absolute',
  top: '0.8cm',
  left: '17.5cm',
  width: '7cm',
  height: '4cm',
  boxSizing: 'border-box',
  padding: 0,
  background: 'transparent',
  display: 'flex',
  flexDirection: 'column',
  fontSize: '0.25cm',
  lineHeight: 1
}}>
  
  <span style={{ display: 'block', margin: 0, padding: 0, marginBottom: '0.3cm' }}>
    {data.buyer.invoiceNo}
  </span>
  
  <span style={{ display: 'block', margin: 0, padding: 0, marginBottom: '0.3cm' }}>
    {data.buyer.challanNo}
  </span>
  
  <span style={{ display: 'block', margin: 0, padding: 0, marginBottom: '0.3cm' }}>
    {data.buyer.invoiceDate}
  </span>
  
  <span style={{ display: 'block', margin: 0, padding: 0, marginBottom: '0.8cm' }}>
    {data.buyer.challanDate}
  </span>
  
  <span style={{ display: 'block', margin: 0, padding: 0, marginBottom: '0.3cm' }}>
    {data.buyer.placeOfSupply}
  </span>
  
  <span style={{ display: 'block', margin: 0, padding: 0, marginBottom: '0.3cm' }}>
    {data.buyer.vehicle}
  </span>
  <span style={{ display: 'block', margin: 0, padding: 0 }}>
    {data.buyer.CrDays}
  </span>
  
</div>

        {/* Receiver/Consignee Boxes */}
        <div style={receiverConsigneeContainer}>
          <div style={receiverConsigneeBox}>
            <div style={receiverConsigneeHeading}></div>
            <div style={receiverConsigneeData}>
              <div>{data.buyer.name}</div>
              <div>{data.buyer.address}</div>
              <div>{data.buyer.gst}</div>
            </div>
          </div>
          <div style={{...receiverConsigneeBox, marginLeft: '0cm'}}>
            <div style={receiverConsigneeHeading}></div>
            <div style={receiverConsigneeData}>
              <div>{invoiceData?.ConsigneeAddress1 || ''}</div>
              <div>{invoiceData?.ConsigneeAddress2 || ''}</div>
              <div>{invoiceData?.ConsigneeGST || ''}</div>
            </div>
          </div>
        </div>
        {/* Item Table Box */}
        <div style={itemTableContainer}>
          <div style={itemTableHeaderRow}>
            <div style={{width: colWidths[0], textAlign: 'center'}}></div>
            <div style={{width: colWidths[1], textAlign: 'center'}}></div>
            <div style={{width: colWidths[2], textAlign: 'center'}}></div>
            <div style={{width: colWidths[3], textAlign: 'center'}}></div>
            <div style={{width: colWidths[4], textAlign: 'center'}}></div>
            <div style={{width: colWidths[5], textAlign: 'center'}}></div>
            <div style={{width: colWidths[6], textAlign: 'center'}}></div>
            <div style={{width: colWidths[7], textAlign: 'center'}}></div>
          </div>
          <div style={itemTableDataArea}>
            {Array.isArray(data.items) && data.items.map((item, idx) => (
              <div key={idx} style={{display: 'flex', flexDirection: 'row', height: 'calc(8.2cm / 10)', fontSize: '11px', alignItems: 'center'}}>
                <div style={{width: colWidths[0], textAlign: 'center'}}>{item.Id}</div>
                <div style={{width: colWidths[1], textAlign: 'center'}}>{item.CodeNo}</div>
                <div style={{width: colWidths[2], textAlign: 'left'}}>{item.Description1} {item.Description2} {item.Description3}</div>
                <div style={{width: colWidths[3], textAlign: 'center'}}>{item.HSNCode}</div>
                <div style={{width: colWidths[4], textAlign: 'right'}}>{item.Qty}</div>
                <div style={{width: colWidths[5], textAlign: 'center'}}>{item.ItemUnit}</div>
                <div style={{width: colWidths[6], textAlign: 'right'}}>{item.Rate}</div>
                <div style={{width: colWidths[7], textAlign: 'right'}}>{item.Amount}</div>
              </div>
            ))}
          </div>
        </div>
        {/* HSN Summary Box */}
        <div style={{
          position: 'absolute',
          top: '18.4cm',
          left: '1.4cm',
          width: '13cm',
          height: '2.4cm',
          background: 'transparent',
          fontSize: '12px',
          color: '#000',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
        }}>
          <table style={{width: '95%', borderCollapse: 'collapse', fontSize: '12px', color: '#000'}}>
            <thead>
              <tr>
                <th style={{textAlign: 'left', fontWeight: 400, fontSize: '13px'}}>HSN</th>
                <th style={{textAlign: 'right', fontWeight: 400, fontSize: '13px'}}>Taxable Value</th>
                <th style={{textAlign: 'right', fontWeight: 400, fontSize: '13px'}}>Rate</th>
                <th style={{textAlign: 'right', fontWeight: 400, fontSize: '13px'}}>CGST</th>
                <th style={{textAlign: 'right', fontWeight: 400, fontSize: '13px'}}>SGST</th>
                <th style={{textAlign: 'right', fontWeight: 400, fontSize: '13px'}}>Total Amount</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(data.HSNSummaryData) && data.HSNSummaryData.map((row, idx) => (
                <tr key={idx}>
                  <td style={{textAlign: 'left'}}>{row.HSNCode}</td>
                  <td style={{textAlign: 'right'}}>{Number(row.TaxableValue).toFixed(2)}</td>
                  <td style={{textAlign: 'right'}}>{Number(row.Rate).toFixed(2)}</td>
                  <td style={{textAlign: 'right'}}>{Number(row.CGSTValue).toFixed(2)}</td>
                  <td style={{textAlign: 'right'}}>{Number(row.SGSTValue).toFixed(2)}</td>
                  <td style={{textAlign: 'right'}}>{Number(row.TotalTax).toFixed(2)}</td>
                </tr>
              ))}
              {/* Total row */}
              {Array.isArray(data.HSNSummaryData) && data.HSNSummaryData.length > 0 && (
                <tr style={{borderTop: '1px solid #000'}}>
                  <td style={{textAlign: 'left', fontWeight: 600}}>Total</td>
                  <td style={{textAlign: 'right', fontWeight: 600}}>{data.HSNSummaryData.reduce((a, b) => a + Number(b.TaxableValue), 0).toFixed(2)}</td>
                  <td></td>
                  <td style={{textAlign: 'right', fontWeight: 600}}>{data.HSNSummaryData.reduce((a, b) => a + Number(b.CGSTValue), 0).toFixed(2)}</td>
                  <td style={{textAlign: 'right', fontWeight: 600}}>{data.HSNSummaryData.reduce((a, b) => a + Number(b.SGSTValue), 0).toFixed(2)}</td>
                  <td style={{textAlign: 'right', fontWeight: 600}}>{data.HSNSummaryData.reduce((a, b) => a + Number(b.TotalTax), 0).toFixed(2)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Amount in Words Box below HSN Summary */}
        <div style={{
          position: 'absolute',
          top: '20.8cm',
          left: '2.5cm',
          width: '12cm',
          height: '2.4cm',
          background: 'transparent',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
        }}>
          <div style={{height: '1.2cm', fontWeight: 500, textTransform: 'uppercase', fontSize: '12px', display: 'flex', alignItems: 'center', letterSpacing: '0.5px'}}>
            {invoiceData?.NetAmountInWords || ''}
          </div>
          <div style={{height: '1.2cm', fontWeight: 500, textTransform: 'uppercase', fontSize: '12px', display: 'flex', alignItems: 'center', letterSpacing: '0.5px'}}>
            {invoiceData?.TaxationAmountInWords || ''}
          </div>
        </div>
        {/* Right Side Summary Boxes */}
        {/* Top box: Summary */}
        <div style={{
          position: 'absolute',
          top: '18.4cm',
          right: '1cm',
          width: '5cm',
          height: '5cm',
          background: 'transparent',
          fontSize: '12px',
          color: '#000',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
        }}>
          <table style={{width: '80%', borderCollapse: 'collapse', fontSize: '12px', color: '#000'}}>
            <tbody>
              <tr>
                <td style={{textAlign: 'left'}}>Total</td>
                <td style={{textAlign: 'right', fontWeight: 600}}>{Number(invoiceData?.GrossAmount || 0).toFixed(2)}</td>
              </tr>
              <tr>
                <td style={{textAlign: 'left'}}>CGST 6 %</td>
                <td style={{textAlign: 'right', fontWeight: 600}}>{Number(invoiceData?.TaxationAmount ? invoiceData.TaxationAmount/2 : 0).toFixed(2)}</td>
              </tr>
              <tr>
                <td style={{textAlign: 'left'}}>SGST 6 %</td>
                <td style={{textAlign: 'right', fontWeight: 600}}>{Number(invoiceData?.TaxationAmount ? invoiceData.TaxationAmount/2 : 0).toFixed(2)}</td>
              </tr>
              <tr>
                <td style={{textAlign: 'left'}}>Rounded Off</td>
                <td style={{textAlign: 'right', fontWeight: 600}}>{Number(invoiceData?.RoundedValue || 0).toFixed(2)}</td>
              </tr>
              <tr>
                <td colSpan={2}><div style={{borderBottom: '2px solid #000', width: '100%', margin: '2px 0'}}></div></td>
              </tr>
              <tr>
                <td style={{textAlign: 'left', fontWeight: 600}}>Invoice Value</td>
                <td style={{textAlign: 'right', fontWeight: 700}}>{Number(invoiceData?.NetAmount || 0).toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        {/* Bottom box: Reserved for future use, empty for now */}
        <div style={{
          position: 'absolute',
          top: '20.8cm',
          left: '14cm',
          width: '6cm',
          height: '2.4cm',
          background: 'transparent',
        }}></div>
        {/* E-Invoice Data Box below Right Side Summary Boxes */}
        <div style={{
          position: 'absolute',
          top: '23.5cm',
          right: '1.5cm',
          width: '8cm',
          height: '2cm',
          background: 'transparent',
          fontSize: '10px',
          color: '#000',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
        }}>
          {Array.isArray(data.EInvoiceData) && data.EInvoiceData.length > 0 && (
            <>
              <div style={{fontWeight: 700, marginBottom: '0.1cm'}}>
                IRN: <span style={{fontWeight: 400, wordBreak: 'break-all'}}>{data.EInvoiceData[0].IRN}</span>
              </div>
              <div style={{fontWeight: 700, marginBottom: '0.1cm'}}>AckNo: <span style={{fontWeight: 400}}>{data.EInvoiceData[0].AckNo}</span></div>
              <div style={{fontWeight: 700}}>AckDt: <span style={{fontWeight: 400}}>{data.EInvoiceData[0].AckDt}</span></div>
            </>
          )}
        </div>
        {/* E-Invoice QR Code */}
        {Array.isArray(data.EInvoiceData) && data.EInvoiceData[0]?.SignedQr && (
          <img
            src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(data.EInvoiceData[0].SignedQr)}&size=150x150`}
            alt="E-Invoice QR"
            style={{
              position: 'absolute',
              top: '2cm',
              left: '1.5cm',
              width: '3cm',
              height: '3cm',
            }}
          />
        )}
        {/* Buyer Signatory Box */}
        <div style={{
          position: 'absolute',
          top: '26cm',
          left: '10cm',
          fontSize: '12px',
          fontWeight: 700,
          color: '#000',
          background: 'transparent',
          textAlign: 'left',
        }}>
          {data.buyer.Signatory || ''}
        </div>
      </div>
      {/* Print Button (not shown when printing) */}
      <div className="no-print" style={{textAlign: 'center', marginTop: 24}}>
        <button onClick={() => window.print()} style={{padding: '10px 32px', fontSize: 16, borderRadius: 5, border: '1px solid #888', background: '#2a41cc', color: '#fff', cursor: 'pointer'}}>Print</button>
      </div>
      {/* Print-only CSS */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .only-print { display: block !important; margin: 0 !important; padding: 0 !important; }
        }
        @media screen {
          .only-print { display: none !important; }
        }
      `}</style>
    </div>
  );
};

// Define the prop types for validation
InvoiceForTrue.propTypes = {
  invoiceArray: PropTypes.arrayOf(PropTypes.shape({
    VoucherNo: PropTypes.string,
    Voucher_Date: PropTypes.string,
    ChallanNo: PropTypes.string,
    PartyName: PropTypes.string,
    GSTPO: PropTypes.string,
    Address2: PropTypes.string,
    GrossAmount: PropTypes.number,
    TaxationAmount: PropTypes.number,
    NetAmount: PropTypes.number,
    PONo: PropTypes.string,
    VehicleNo: PropTypes.string,
    GrossAmountInWords: PropTypes.string,
    TaxationAmountInWords: PropTypes.string,
    NetAmountInWords: PropTypes.string,
    ConsigneeAddress1: PropTypes.string,
    ConsigneeAddress2: PropTypes.string,
    ConsigneeAddress3: PropTypes.string,
    RoundedValue: PropTypes.number,
    PlaceOfSupply: PropTypes.string
  })),
  firmArray: PropTypes.arrayOf(PropTypes.shape({
    GST: PropTypes.string,
    FirmName: PropTypes.string,
    Address1: PropTypes.string,
    Address2: PropTypes.string,
    MobileNo: PropTypes.string,
    BankName: PropTypes.string,
    AccountNo: PropTypes.string,
    IFSC: PropTypes.string,
    MsmeNo: PropTypes.string
  })),
  items: PropTypes.arrayOf(PropTypes.shape({
    Counter: PropTypes.number,
    SNo: PropTypes.number,
    Id: PropTypes.string,
    CodeNo: PropTypes.string,
    Description1: PropTypes.string,
    Description2: PropTypes.string,
    Description3: PropTypes.string,
    HSNCode: PropTypes.string,
    Qty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    Rate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ItemUnit: PropTypes.string,
    Amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    ItemType: PropTypes.string,
    Tax: PropTypes.string
  })),
  taxBreakup: PropTypes.arrayOf(PropTypes.shape({
    TaxName: PropTypes.string,
    AddAmt: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  })),
  taxSummary: PropTypes.arrayOf(PropTypes.shape({
    Name: PropTypes.string,
    Amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  })),
  totals: PropTypes.shape({
    taxable: PropTypes.number,
    tax: PropTypes.number,
    cgst: PropTypes.number,
    sgst: PropTypes.number,
    roundOff: PropTypes.number,
    grandTotal: PropTypes.number
  })
};

// Define default props
InvoiceForTrue.defaultProps = {
  invoiceArray: [],
  firmArray: [],
  items: null, // null will make the component use the default items in the data object
  taxBreakup: null,
  taxSummary: null,
  totals: null
};

export default InvoiceForTrue;
